import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CheckCircle2, ClipboardCheck } from 'lucide-react'
import { useCurrency } from '@/contexts/CurrencyContext'
import { compactCurrency } from '@/components/admin/dashboard/dashboardFormat'
import { formatDateTimeHuman } from '@/lib/utils'
import type { AdvancedStockReport } from '@/types'
import * as Help from '@/lib/inventory-report-help'
import { MetricHint, StatTile, fmtQty, fmtQtyUnit } from './primitives'

/**
 * "What are you losing?" — one honest headline (waste + what counts found
 * missing), with waste and count findings itemised below it. Overages and
 * vendor returns are shown as their own lines, never netted against losses.
 */
export function LossesCard({ losses }: { losses: AdvancedStockReport['losses'] }) {
  const { formatCurrency } = useCurrency()
  const wasteRows = losses.waste_rows ?? []
  const countRows = losses.count_rows ?? []
  const totalLost = losses.waste_value + losses.count_shrinkage_value
  const nothingLost = totalLost <= 0.005 && losses.count_found_value <= 0.005 && countRows.length === 0

  return (
    <Card className={totalLost > 0.005 ? 'border-danger-border' : undefined}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base font-semibold">What are you losing?</CardTitle>
          <MetricHint text={Help.LOSSES_HEADLINE} label="losses" />
        </div>
        <CardDescription className="text-xs">
          Stock thrown away or damaged, plus stock your counts found missing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {nothingLost ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-sm font-medium">No losses recorded this period.</p>
            <p className="text-xs mt-1 text-muted-foreground/80">
              Write off spoiled stock with “Issue stock → Spoilage / Waste”, and count items regularly —
              losses only show up here when they are recorded.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatTile
              label="Lost this period"
              value={compactCurrency(totalLost)}
              tone={totalLost > 0.005 ? 'text-red-600' : ''}
              hint={Help.LOSSES_HEADLINE}
            />
            <StatTile
              label="Thrown away / damaged"
              value={compactCurrency(losses.waste_value)}
              hint={Help.LOSSES_WASTE}
              sub={losses.waste_pct != null ? `${losses.waste_pct.toFixed(1)}% of what left the shelf` : undefined}
            />
            <StatTile
              label="Counts found missing"
              value={compactCurrency(losses.count_shrinkage_value)}
              hint={Help.LOSSES_SHRINKAGE}
            />
            <StatTile
              label="Counts found extra"
              value={compactCurrency(losses.count_found_value)}
              hint={Help.LOSSES_FOUND}
              sub={losses.vendor_returns_value > 0.005
                ? `Also ${compactCurrency(losses.vendor_returns_value)} returned to vendors (not a loss)`
                : undefined}
            />
          </div>
        )}

        {/* F-CTRL-N74: the part of the waste that came off the floor. */}
        {losses.order_waste && (losses.order_waste.void_count > 0 || losses.order_waste.refund_count > 0) && (
          <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
            <p className="font-medium">Dishes thrown away</p>
            <p className="text-xs text-muted-foreground">
              {losses.order_waste.void_count > 0 && (
                <>{losses.order_waste.void_count} voided after they were made ({formatCurrency(losses.order_waste.void_cost)} of ingredients, {formatCurrency(losses.order_waste.void_menu_value)} on the menu)</>
              )}
              {losses.order_waste.void_count > 0 && losses.order_waste.refund_count > 0 && ' · '}
              {losses.order_waste.refund_count > 0 && (
                <>{losses.order_waste.refund_count} refunded and not put back ({formatCurrency(losses.order_waste.refund_cost)} of ingredients)</>
              )}
              . Voided dishes are already in the waste figure above; see the Void Log for each one.
            </p>
          </div>
        )}

        {wasteRows.length > 0 && (
          <div>
            <div className="mb-2 flex items-center gap-2">
              <h4 className="text-sm font-semibold">Waste log</h4>
              <MetricHint text={Help.LOSSES_WASTE} label="waste log" />
              <Badge variant="destructive" className="text-xs">
                {losses.waste_rows_total} write-off{losses.waste_rows_total !== 1 ? 's' : ''} · {formatCurrency(losses.waste_value)}
              </Badge>
            </div>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full min-w-[680px] text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-xs text-muted-foreground">
                    <th className="px-4 py-2.5 text-left font-medium">When</th>
                    <th className="px-4 py-2.5 text-left font-medium">Item</th>
                    <th className="px-4 py-2.5 text-right font-medium">How much</th>
                    <th className="px-4 py-2.5 text-left font-medium">Reason</th>
                    <th className="px-4 py-2.5 text-right font-medium">Cost you</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {wasteRows.map((w, i) => (
                    <tr key={`${w.item_name}-${w.occurred_at}-${i}`} className="hover:bg-muted/30">
                      <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{formatDateTimeHuman(w.occurred_at)}</td>
                      <td className="px-4 py-2.5">
                        <span className="font-medium">{w.item_name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{w.category}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtQtyUnit(w.qty, w.unit)}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className="text-xs font-normal">{w.reason}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-red-600">{formatCurrency(w.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {losses.waste_rows_total > wasteRows.length && (
                <p className="px-4 py-2 text-xs text-muted-foreground">
                  Showing the {wasteRows.length} biggest by value — all {losses.waste_rows_total} are in the Excel export.
                </p>
              )}
            </div>
          </div>
        )}

        <div>
          <div className="mb-2 flex items-center gap-2">
            <h4 className="text-sm font-semibold">What your counts found</h4>
            <MetricHint text={Help.LOSSES_COUNT_TABLE} label="count results" />
            {countRows.length > 0 && (
              <Badge variant="outline" className="text-xs">
                {losses.count_rows_total} count{losses.count_rows_total !== 1 ? 's' : ''} this period
              </Badge>
            )}
          </div>
          {countRows.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground border rounded-lg">
              <ClipboardCheck className="w-7 h-7 mx-auto mb-2 opacity-40" />
              <p className="text-sm font-medium">No counts recorded this period.</p>
              <p className="text-xs mt-1 text-muted-foreground/80 max-w-md mx-auto">
                Counting is how you catch losses: count what’s on the shelf and the system updates the
                stock and shows you what went missing. Start with the list under “What needs your attention”.
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-xs text-muted-foreground">
                    <th className="px-4 py-2.5 text-left font-medium">Item</th>
                    <th className="px-4 py-2.5 text-left font-medium">Counted on</th>
                    <th className="px-4 py-2.5 text-right font-medium">You counted</th>
                    <th className="px-4 py-2.5 text-right font-medium">System expected</th>
                    <th className="px-4 py-2.5 text-right font-medium">Difference</th>
                    <th className="px-4 py-2.5 text-left font-medium">Since the last count</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {countRows.map(r => {
                    const tone = Math.abs(r.variance_qty) <= 1e-9
                      ? 'text-success-fg'
                      : r.variance_qty < 0 ? 'text-red-600' : 'text-warning-fg'
                    const sinceBits: string[] = []
                    if (r.sold_since_prev != null && Math.abs(r.sold_since_prev) > 1e-9) sinceBits.push(`sold ${fmtQty(r.sold_since_prev)}`)
                    if (r.purchased_since_prev != null && Math.abs(r.purchased_since_prev) > 1e-9) sinceBits.push(`bought ${fmtQty(r.purchased_since_prev)}`)
                    if (r.wasted_since_prev != null && Math.abs(r.wasted_since_prev) > 1e-9) sinceBits.push(`wasted ${fmtQty(r.wasted_since_prev)}`)
                    return (
                      <tr key={`${r.item_id}-${r.counted_at}`} className="hover:bg-muted/30">
                        <td className="px-4 py-2.5">
                          <span className="font-medium">{r.item_name}</span>
                          <span className="ml-2 text-xs text-muted-foreground">{r.category}</span>
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{formatDateTimeHuman(r.counted_at)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fmtQtyUnit(r.counted_qty, r.unit)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{fmtQtyUnit(r.book_qty_before, r.unit)}</td>
                        <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${tone}`}>
                          {Math.abs(r.variance_qty) <= 1e-9
                            ? 'Matched'
                            : `${r.variance_qty > 0 ? '+' : ''}${fmtQtyUnit(r.variance_qty, r.unit)}`}
                          {Math.abs(r.variance_value) > 0.005 && (
                            <div className="text-xs font-normal">
                              {r.estimated ? '≈ ' : ''}{r.variance_value < 0 ? '-' : '+'}{formatCurrency(Math.abs(r.variance_value))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">
                          {r.prev_count_at == null
                            ? 'First count of this item'
                            : sinceBits.length > 0 ? sinceBits.join(' · ') : 'No recorded movement'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {losses.count_rows_total > countRows.length && (
                <p className="px-4 py-2 text-xs text-muted-foreground">
                  Showing the {countRows.length} biggest differences — all {losses.count_rows_total} are in the Excel export.
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
